import { useState } from 'react';
import { FileIcon, Image, FileText, Download, Trash2, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

export default function AttachmentList({ annotationId, attachments, onDelete, canDelete = false }) {
  const [deleting, setDeleting] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleDelete = async (filename) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;

    setDeleting(filename);
    try {
      await api.delete(`/annotations/${annotationId}/attachments/${filename}`);
      toast.success('Pièce jointe supprimée');
      if (onDelete) {
        onDelete(filename);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    if (mimeType === 'application/pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    return <FileIcon className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (mimeType) => mimeType.startsWith('image/');

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-3 space-y-2">
        <p className="text-xs font-medium text-gray-700">Pièces jointes ({attachments.length})</p>
        <div className="grid grid-cols-1 gap-2">
          {attachments.map((attachment, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {getFileIcon(attachment.mimeType)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {attachment.originalName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(attachment.size)} • {new Date(attachment.uploadedAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 ml-2">
                {isImage(attachment.mimeType) && (
                  <button
                    onClick={() => setPreview(attachment)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Aperçu"
                  >
                    <Image className="w-4 h-4" />
                  </button>
                )}
                <a
                  href={attachment.url}
                  download={attachment.originalName}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Télécharger"
                >
                  <Download className="w-4 h-4" />
                </a>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(attachment.filename)}
                    disabled={deleting === attachment.filename}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Supprimer"
                  >
                    {deleting === attachment.filename ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de prévisualisation des images */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setPreview(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setPreview(null)}
              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            <img
              src={preview.url}
              alt={preview.originalName}
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 text-center">
              <p className="text-white font-medium">{preview.originalName}</p>
              <p className="text-gray-300 text-sm">{formatFileSize(preview.size)}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
